import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Shared outer wrapper for registry timeline cards (application, application-report,
 * planting, note, visit, harvest, yield-estimation). The recommendation card intentionally
 * uses its own highlighted variant and does not use this component.
 *
 * `expanded`/`onToggle` are only passed on mobile, for collapsible entries (injected by
 * `Registries`' accordion state via `cloneElement`) — when `onToggle` is absent (desktop, or
 * a non-collapsible mobile entry) the card renders exactly as before: header + menu always
 * visible, `children` and `footer` always shown, no chevron.
 */
export const RegistryCard = ({
    header,
    menu,
    footer,
    expanded,
    onToggle,
    badge,
    highlightColor,
    children,
}: {
    /** Icon + title content of the header row — unchanged per card. */
    header: ReactNode;
    /** The card's "more options" IconButton + Menu, if any. */
    menu?: ReactNode;
    /** The "Registrado por X - date" / "Sincronizado desde FieldView" caption. */
    footer?: ReactNode;
    /** Mobile-only collapse state; absent means always-expanded (desktop behavior). */
    expanded?: boolean;
    onToggle?: () => void;
    /** Category badge straddling the top-left border (e.g. "Reclamo"). */
    badge?: ReactNode;
    /** Highlighted variant: a 2px border in this color instead of the default stroke. */
    highlightColor?: string;
    children: ReactNode;
}) => (
    <Stack
        spacing={2}
        sx={{
            bgcolor: 'background.paper',
            border: highlightColor ? 2 : 1,
            borderColor: highlightColor ?? 'primary.mui.stroke',
            borderRadius: 2,
            boxShadow: '0px 1px 0.5px rgba(0, 0, 0, 0.14)',
            p: 2,
            // Room for the badge straddling the top border; on mobile the timeline's gap between
            // entries is too tight to clear it, so push the card down too (as the recommendation card does).
            ...(badge ? { position: 'relative', pt: 3, mt: onToggle ? 2.5 : 0 } : {}),
        }}
    >
        <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            onClick={onToggle}
            sx={onToggle ? { cursor: 'pointer' } : undefined}
        >
            {/* Inside the header row (not a direct card child) so it doesn't shift the card's
                Stack spacing; positioned against the card itself. */}
            {badge && <Box sx={{ position: 'absolute', top: -14, left: 12 }}>{badge}</Box>}
            <Stack direction='row' spacing={0.5} alignItems='center'>
                {header}
            </Stack>
            {onToggle ? (
                <Stack direction='row' alignItems='center' spacing={0.5}>
                    {expanded && (
                        <Stack
                            direction='row'
                            alignItems='center'
                            onClick={(event) => event.stopPropagation()}
                            sx={{ display: 'flex' }}
                        >
                            {menu}
                        </Stack>
                    )}
                    <Stack
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggle();
                        }}
                        sx={{ cursor: 'pointer', display: 'flex' }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </Stack>
                </Stack>
            ) : (
                menu
            )}
        </Stack>

        {!onToggle || expanded ? (
            <>
                {children}
                {footer}
            </>
        ) : (
            footer
        )}
    </Stack>
);
